import { defineStore } from 'pinia';
import axios from '../../axios';
import { useAuthStore } from './auth';

export const useAddressStore = defineStore('address', {
  state: () => ({
    addresses: [],
    pickUpAddresses: [],
    activeAddress: {
      id: null,
      postal_code: '',
      name: '',
      street_address1: '',
      street_address2: '',
      suburbs: '',
      city: '',
    },
    eligibleAddress: [],
    eligibityError:'',
    eligiblePostalCodes: [],
    deliveryPointsMap: {}, // 存储邮编与配送费的映射
    // Delivery pricing policy (staff-editable, from GET /delivery-config/).
    // Mirrors the server; the server remains authoritative at order time.
    deliveryConfig: {
      flat_delivery_fee: 0,
      free_delivery_threshold: 0,
      minimum_order_amount: 0,
      use_zone_pricing: false,
      free_delivery_store_credit: false,
    },
  }),

  getters: {
    getActiveAddress(state) {
      return state.activeAddress;
    },
    getAllAddresses(state) {
      return state.addresses;
    },
    getEligibleAddresses(state) { // Optional: Getter for eligible addresses
      return state.eligibleAddress;
    },
    getDeliveryFee: (state) => (postalCode) => {
      // 根据邮编获取配送费，如果找不到则返回默认值
      return state.deliveryPointsMap[parseInt(postalCode)] || 0;
    },
  },

  actions: {
    // Load the delivery pricing policy (flat fee / free threshold / min order / zones).
    async fetchDeliveryConfig() {
      try {
        const { data } = await axios.get('/delivery-config/');
        this.deliveryConfig = {
          flat_delivery_fee: parseFloat(data.flat_delivery_fee) || 0,
          free_delivery_threshold: parseFloat(data.free_delivery_threshold) || 0,
          minimum_order_amount: parseFloat(data.minimum_order_amount) || 0,
          use_zone_pricing: !!data.use_zone_pricing,
          free_delivery_store_credit: !!data.free_delivery_store_credit,
        };
      } catch (error) {
        console.error('Failed to fetch delivery config:', error);
      }
      return this.deliveryConfig;
    },

    // Quote the delivery fee for the storefront. Mirrors the backend
    // OrderingSettings.delivery_fee_for(); the server recomputes authoritatively
    // at order time, so this is for display only.
    // `payableFood` = food subtotal after discount (what the customer pays).
    quoteDeliveryFee({ orderType, postalCode, payableFood }) {
      if (String(orderType || '').toUpperCase() !== 'DELIVERY') return 0;
      const cfg = this.deliveryConfig;
      const threshold = cfg.free_delivery_threshold || 0;
      if (threshold > 0 && Number(payableFood) >= threshold) return 0;
      if (cfg.use_zone_pricing && postalCode) {
        const zone = this.deliveryPointsMap[parseInt(postalCode)];
        if (zone !== undefined && zone !== null) return zone;
      }
      return cfg.flat_delivery_fee || 0;
    },

    // Define eligible postal codes as a constant
    async getEligiblePostalCodes() {
    try {
      let url = '/menu/delivery-point/';
      let allPostalCodes = [];
      let deliveryPoints = {};

      while (url) {
        const response = await axios.get(url);
        const data = response.data;
        // 存储每个邮编对应的配送费
        data.results.forEach(item => {
          const postalCode = parseInt(item.postal_code);
          allPostalCodes.push(postalCode);
          deliveryPoints[postalCode] = parseFloat(item.price);
        });
        url = data.next;
      }

      this.eligiblePostalCodes = allPostalCodes;
      this.deliveryPointsMap = deliveryPoints;
      const authStore = useAuthStore();
      if (authStore.user) authStore.deliveryEligibilityCheck();   // signed-out visitors just get the list
    } catch (error) {
      console.error('Failed to fetch eligible postal codes:', error);
    }
  },

    fetchEligiblePostalCodes(){
      return this.eligiblePostalCodes;
    },

    // Method to update eligibleAddress based on postal codes
    updateEligibleAddress() {
      console.log(this.eligiblePostalCodes);
      // 过滤出符合配送条件的地址
      const filteredAddresses = this.addresses.filter(address =>
        this.eligiblePostalCodes.includes(parseInt(address.postal_code))
      );
      
      // 按id降序排序（假设id越大越新），然后取前5个
      this.eligibleAddress = filteredAddresses
        .sort((a, b) => b.id - a.id)
        .slice(0, 5);
    },

    async fetchAddresses() {
      try {
        // 获取所有地址数据，处理分页
        let url = '/user-address/';
        let allAddresses = [];
        
        while (url) {
          const response = await axios.get(url);
          const data = response.data;
          allAddresses = allAddresses.concat(data.results);
          url = data.next; // 获取下一页的URL
        }
        
        // 确保按id倒序排列（最新的在前）
        this.addresses = allAddresses.sort((a, b) => b.id - a.id);
        this.updateEligibleAddress();
      } catch (error) {
        console.error('Failed to fetch addresses:', error);
      }
      try {
        const response = await axios.get('/menu/pickup-location/');
        this.pickUpAddresses = response.data.results;
      } catch (error) {
        console.error('Failed to fetch addresses:', error);
      }
    },

    initializeActiveAddress() {
      this.activeAddress = {
        id: null,
        postal_code: '',
        name: '',
        street_address1: '',
        street_address2: '',
        suburbs: '',
        city: '',
      };
    },

    setActiveAddress(address) {
      this.activeAddress = { ...address };
    },

    async addAddress(addressData) {
      this.eligibityError = "";
      let response;
      try {
        response = await axios.post('/user-address/', addressData);
      } catch (error) {
        console.error('Failed to add address:', error);
        throw error;
      }
      // Saved. A hiccup in the bookkeeping below must not tell the customer it failed
      // (they'd save it again and end up with duplicates).
      this.addresses.push(response.data);
      try {
        this.updateEligibleAddress(); // Update eligibleAddress after adding
        this.initializeActiveAddress();
      } catch (e) { console.warn('address saved; refresh step failed', e); }
    },

    async updateAddress(addressId, updatedData) {
      try {
        const response = await axios.put(`/user-address/${addressId}/`, updatedData);
        const index = this.addresses.findIndex(addr => addr.id === addressId);
        if (index !== -1) {
          this.addresses.splice(index, 1, response.data);
          this.updateEligibleAddress();
        }
        this.initializeActiveAddress();
      } catch (error) {
        console.error('Failed to update address:', error);
        throw error;
      }
    },

    async deleteAddress(addressId) {
      try {
        await axios.delete(`/user-address/${addressId}/`);
        this.addresses = this.addresses.filter(addr => addr.id !== addressId);
        this.updateEligibleAddress();
      } catch (error) {
        console.error('Failed to delete address:', error);
        throw error;
      }
    },

    async saveActiveAddress() {
      if (this.activeAddress.id) {
        await this.updateAddress(this.activeAddress.id, this.activeAddress);
      } else {
        await this.addAddress(this.activeAddress);
      }
    },
    checkDeliveryEligibility(postal_code){
      if(this.eligiblePostalCodes.includes(parseInt(postal_code))){
        this.eligibityError = "Delivery available";
      }
      else{
        this.eligibityError = "Delivery not available";
      }
      return;
    }
  },
});

// Setup caching for the store, excluding error states
